import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiVideo,
  FiRadio,
  FiDollarSign,
  FiLoader,
  FiSettings,
} from "react-icons/fi";
import "@livekit/components-styles";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import MintSpaceAppWindow from "../components/MintSpaceAppWindow";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import InlineMessage from "../components/ds/InlineMessage";
import TextField from "../components/ds/TextField";
import LiveStreamBroadcastRoom from "../components/LiveStreamBroadcastRoom";
import LiveStreamDeviceModal from "../components/LiveStreamDeviceModal";
import { LIVEKIT_URL } from "../config/env";
import {
  getLiveStreamSession,
  startLiveStreamSession,
  endLiveStreamSession,
  getLiveStreamToken,
} from "../api/mintSpaceLiveStream";

/**
 * Live Stream app — hosts publish camera/mic/screen via LiveKit; members watch.
 */
const FILLED = "!bg-app-live-base !text-app-live-on";

export default function MintSpaceLiveStreamPage({
  isDark,
  setToast,
  mintSpace,
  isAdmin = false,
  appInstanceId,
  onCreateProduct,
  onOpenProperties,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket } = useSocket();
  const stagePromptOpening = useRef(false);

  const openStageDevicePrompt = useCallback(() => {
    stagePromptOpening.current = true;
    setDevicePrompt({ variant: "stage", draft: null });
  }, []);

  const stageSpeakerSetupPendingRef = useRef(false);
  const mintSpaceId = mintSpace?._id;
  const displayName =
    user?.businessName || user?.username || user?.profile?.businessName || "Member";
  const userId = user?._id || user?.id;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [canHost, setCanHost] = useState(false);
  const [livekitConfigured, setLivekitConfigured] = useState(false);
  const [session, setSession] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  /** @type {null | { token: string, url: string, role: string, hostUserId: string, canPublish: boolean, devices?: { camera: boolean, microphone: boolean } }} */
  const [connection, setConnection] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  /** Bumps when stage token is refreshed so LiveKitRoom reconnects with publish rights. */
  const [liveKitSessionKey, setLiveKitSessionKey] = useState(0);
  /** @type {null | { variant: 'host'|'rejoin-host'|'stage', draft: object }} */
  const [devicePrompt, setDevicePrompt] = useState(null);

  const applyConnection = (draft, devices) => {
    setConnection({
      ...draft,
      devices: {
        camera: !!devices?.camera,
        microphone: !!devices?.microphone,
      },
    });
    setLiveKitSessionKey((k) => k + 1);
  };

  const confirmDevicePrompt = async ({ camera, microphone }) => {
    if (!devicePrompt) {
      stagePromptOpening.current = false;
      return;
    }
    const { variant, draft } = devicePrompt;

    if (variant === "stage") {
      if (!mintSpaceId || !appInstanceId) return;
      setBusy(true);
      try {
        const res = await getLiveStreamToken(mintSpaceId, {
          instanceId: appInstanceId,
          asPublisher: false,
        });
        if (!res.success || !res.data?.token || !res.data.canPublish) {
          throw new Error(
            res.message ||
              t("liveStream.stageReconnectFailed") ||
              "Could not join the stream as a speaker"
          );
        }
        setSession(res.data.session);
        setJoinRequests(res.data.session?.joinRequests || []);
        applyConnection(
          {
            token: res.data.token,
            url: res.data.livekitUrl || LIVEKIT_URL,
            role: res.data.role || "stage",
            hostUserId: res.data.hostUserId || connection?.hostUserId,
            canPublish: true,
          },
          { camera, microphone }
        );
        setDevicePrompt(null);
        stagePromptOpening.current = false;
        stageSpeakerSetupPendingRef.current = false;
        setToast?.(
          t("liveStream.stageGranted") ||
            "You joined as a speaker — use the controls to adjust devices"
        );
      } catch (err) {
        if (setToast) {
          setToast(err.response?.data?.message || err.message || "Could not join as speaker");
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!draft) {
      setDevicePrompt(null);
      stagePromptOpening.current = false;
      return;
    }
    applyConnection(draft, { camera, microphone });
    setDevicePrompt(null);
    stagePromptOpening.current = false;
    if (variant === "host" || variant === "rejoin-host") {
      setToast?.(t("liveStream.youAreLive") || "You are live");
    }
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  const isLive = session?.status === "live";

  const refreshSession = useCallback(async () => {
    if (!mintSpaceId || !appInstanceId) return null;
    const res = await getLiveStreamSession(mintSpaceId, appInstanceId);
    if (!res.success) throw new Error(res.message || "Failed to load session");
    setSession(res.data.session);
    setCanHost(!!res.data.canHost);
    setLivekitConfigured(!!res.data.livekitConfigured);
    if (res.data.session?.title) setTitleDraft(res.data.session.title);
    if (res.data.session?.joinRequests) setJoinRequests(res.data.session.joinRequests);
    return res.data;
  }, [mintSpaceId, appInstanceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mintSpaceId || !appInstanceId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        await refreshSession();
      } catch (err) {
        if (!cancelled && setToast) {
          setToast(
            err.message || t("liveStream.failedToLoad") || "Failed to load live stream"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpaceId, appInstanceId, refreshSession, setToast, t]);

  useEffect(() => {
    if (!mintSpaceId || !appInstanceId || connection || isLive) return undefined;
    const id = setInterval(() => {
      refreshSession().catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [mintSpaceId, appInstanceId, connection, isLive, refreshSession]);

  const handleGoLive = async () => {
    if (!mintSpaceId || !appInstanceId) return;
    setBusy(true);
    try {
      const res = await startLiveStreamSession(mintSpaceId, {
        instanceId: appInstanceId,
        title: titleDraft.trim(),
      });
      if (!res.success) throw new Error(res.message || "Failed to start");
      setSession(res.data.session);
      setJoinRequests(res.data.session?.joinRequests || []);
      setDevicePrompt({
        variant: "host",
        draft: {
          token: res.data.token,
          url: res.data.livekitUrl || LIVEKIT_URL,
          role: res.data.role || "host",
          hostUserId: res.data.hostUserId || userId,
          canPublish: true,
        },
      });
    } catch (err) {
      if (setToast) {
        setToast(err.response?.data?.message || err.message || "Failed to go live");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleJoinAsViewer = async () => {
    if (!mintSpaceId || !appInstanceId) return;
    setBusy(true);
    try {
      const res = await getLiveStreamToken(mintSpaceId, {
        instanceId: appInstanceId,
        asPublisher: false,
      });
      if (!res.success) throw new Error(res.message || "Failed to join");
      setSession(res.data.session);
      setJoinRequests(res.data.session?.joinRequests || []);
      setConnection({
        token: res.data.token,
        url: res.data.livekitUrl || LIVEKIT_URL,
        role: res.data.role || "viewer",
        hostUserId: res.data.hostUserId,
        canPublish: !!res.data.canPublish,
        devices: { camera: false, microphone: false },
      });
    } catch (err) {
      if (setToast) {
        setToast(err.response?.data?.message || err.message || "Failed to join live");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRejoinAsHost = async () => {
    if (!mintSpaceId || !appInstanceId) return;
    setBusy(true);
    try {
      const res = await getLiveStreamToken(mintSpaceId, {
        instanceId: appInstanceId,
        asPublisher: true,
      });
      if (!res.success) throw new Error(res.message || "Failed to rejoin");
      setSession(res.data.session);
      setJoinRequests(res.data.session?.joinRequests || []);
      setDevicePrompt({
        variant: "rejoin-host",
        draft: {
          token: res.data.token,
          url: res.data.livekitUrl || LIVEKIT_URL,
          role: res.data.role || "host",
          hostUserId: res.data.hostUserId || userId,
          canPublish: true,
        },
      });
    } catch (err) {
      if (setToast) {
        setToast(err.response?.data?.message || err.message || "Failed to rejoin");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSessionUpdate = useCallback((nextSession) => {
    if (!nextSession) return;
    setSession((prev) => ({ ...(prev || {}), ...nextSession }));
    if (nextSession.joinRequests) {
      setJoinRequests(nextSession.joinRequests);
    }
  }, []);

  useEffect(() => {
    if (!socket || !userId || !mintSpaceId || !appInstanceId) return undefined;

    const onStageDecision = (payload) => {
      if (payload?.userId != null && String(payload.userId) !== String(userId)) return;
      if (payload?.mintSpaceId != null && String(payload.mintSpaceId) !== String(mintSpaceId)) {
        return;
      }
      if (payload?.instanceId != null && payload.instanceId !== appInstanceId) return;

      if (payload?.session) handleSessionUpdate(payload.session);

      if (payload?.accept) {
        stageSpeakerSetupPendingRef.current = true;
        openStageDevicePrompt();
        if (setToast) {
          setToast(
            t("liveStream.devicePromptStageOpen") ||
              "Choose your camera and microphone, then continue."
          );
        }
      }
    };

    socket.on("livestream_stage_decision", onStageDecision);
    return () => socket.off("livestream_stage_decision", onStageDecision);
  }, [
    socket,
    userId,
    mintSpaceId,
    appInstanceId,
    handleSessionUpdate,
    openStageDevicePrompt,
    setToast,
    t,
  ]);

  /** One-time prompt if session already lists user as speaker (e.g. missed socket). */
  useEffect(() => {
    if (!userId || !connection || connection.role === "stage") return;
    const onStage = (session?.stagePublishers || []).some(
      (id) => String(id) === String(userId)
    );
    if (!onStage || stageSpeakerSetupPendingRef.current) return;
    stageSpeakerSetupPendingRef.current = true;
    openStageDevicePrompt();
  }, [userId, connection, session?.stagePublishers, openStageDevicePrompt]);

  const handleEndLive = async () => {
    if (!mintSpaceId || !appInstanceId) return;
    setBusy(true);
    try {
      const res = await endLiveStreamSession(mintSpaceId, appInstanceId);
      if (!res.success) throw new Error(res.message || "Failed to end");
      setConnection(null);
      setSession(res.data.session || { status: "ended", title: "", roomName: null });
      await refreshSession();
      if (setToast) setToast(t("liveStream.streamEnded") || "Live ended");
    } catch (err) {
      if (setToast) {
        setToast(err.response?.data?.message || err.message || "Failed to end live");
      }
    } finally {
      setBusy(false);
    }
  };

  const menuItems = useMemo(
    () =>
      [
        isAdmin &&
          onOpenProperties && {
            id: "properties",
            label: t("mintSpace.appProperties") || "Properties",
            icon: FiSettings,
            onClick: onOpenProperties,
          },
        isAdmin &&
          onCreateProduct && {
            id: "create-product",
            label: t("mintSpace.createProduct") || "Create product",
            icon: FiDollarSign,
            onClick: onCreateProduct,
          },
      ].filter(Boolean),
    [isAdmin, onCreateProduct, onOpenProperties, t]
  );

  const subtitle = isLive
    ? session?.title || t("liveStream.liveNow") || "Live now"
    : t("liveStream.offline") || "Offline";

  const inRoom = Boolean(connection?.token && connection?.url);

  return (
    <MintSpaceAppWindow
      isDark={isDark}
      app="live"
      icon={FiVideo}
      iconGradient="from-red-500 to-pink-500"
      title={t("mintSpace.appLiveName") || "Live Stream"}
      subtitle={subtitle}
      menuItems={menuItems}
      // Only a running room fills the window by itself; the screens before it scroll, with
      // room at the bottom on desktop so their last button clears the Messages bar.
      bodyOverflow={inRoom ? "hidden" : "auto"}
      bodyClassName={`flex min-h-0 flex-1 flex-col ${inRoom ? "" : "lg:pb-[80px]"}`}
    >
      {loading ? (
        <div className={`flex flex-1 items-center justify-center gap-2 text-subheadline ${mutedText}`}>
          <FiLoader className="h-4 w-4 animate-spin" />
          {t("common.loading") || "Loading…"}
        </div>
      ) : !appInstanceId ? (
        <div
          className={`flex flex-1 items-center justify-center p-6 text-center text-subheadline ${mutedText}`}
        >
          {t("liveStream.missingInstance") ||
            "Open this app from the sidebar to start streaming."}
        </div>
      ) : inRoom ? (
        <div className="relative flex min-h-0 flex-1 flex-col" data-lk-theme="default">
          {connection.role === "host" && (
            <div
              className={`flex shrink-0 items-center gap-2 border-b px-3 py-2 ${
                "border-separator-default bg-bg-inset"
              }`}
            >
              <span className="inline-flex items-center gap-1.5 text-footnote-semibold text-status-danger">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                {t("liveStream.broadcasting") || "Broadcasting"}
              </span>
            </div>
          )}
          <LiveStreamBroadcastRoom
            liveKitSessionKey={liveKitSessionKey}
            token={connection.token}
            serverUrl={connection.url}
            role={connection.role}
            hostUserId={connection.hostUserId}
            userId={userId}
            displayName={displayName}
            mintSpaceId={mintSpaceId}
            instanceId={appInstanceId}
            isDark={isDark}
            joinRequests={joinRequests}
            onJoinRequestsChange={setJoinRequests}
            onEndLive={handleEndLive}
            busy={busy}
            canPublish={connection.canPublish}
            onDisconnected={() => setConnection(null)}
            publishCamera={!!connection.devices?.camera}
            publishMicrophone={!!connection.devices?.microphone}
            onOpenStageDevicePrompt={openStageDevicePrompt}
            setToast={setToast}
            stagePublisherIds={session?.stagePublishers || []}
            onSessionUpdate={handleSessionUpdate}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-space-16">
          {!livekitConfigured && (canHost || isAdmin) && (
            <InlineMessage tone="warning">{t("liveStream.notConfigured")}</InlineMessage>
          )}

          <section className="flex flex-col items-center gap-space-16 rounded-radius-lg bg-bg-raised p-space-16 min-[600px]:p-space-24">
            <div className="flex h-[204px] w-full items-center justify-center rounded-radius-lg bg-bg-inset text-label-tertiary">
              <FiRadio className="size-8" />
            </div>
            <div className="flex flex-col items-center gap-space-8 text-center">
              <span className="flex size-[64px] items-center justify-center rounded-full bg-app-live-deep text-app-live-soft">
                <Icon name="video" size={32} />
              </span>
              <h2 className="text-title-2 text-label-primary">
                {isLive ? t("liveStream.liveNow") : t("liveStream.readyToStream")}
              </h2>
              <p className="text-subheadline text-label-secondary">
                {isLive ? session?.title || t("liveStream.tapToWatch") : t("liveStream.shareDevices")}
              </p>
            </div>

            {(canHost || isAdmin) && !isLive && (
              <TextField
                label={t("liveStream.titleLabel")}
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                maxLength={120}
                placeholder={t("liveStream.titlePlaceholder")}
              />
            )}

            <div className="flex flex-wrap justify-center gap-space-8">
              {[
                { icon: "video", label: t("liveStream.camera") },
                { icon: "mic", label: t("liveStream.mic") },
                { icon: "image", label: t("liveStream.screen") },
              ].map(({ icon, label }) => (
                <Badge key={label} className="!h-[40px] gap-space-8 !px-space-16 !text-caps-button-m">
                  <Icon name={icon} size={20} />
                  {label}
                </Badge>
              ))}
            </div>

            <div className="flex w-full flex-col gap-space-8">
              {(canHost || isAdmin) && !isLive && (
                <Button
                  fullWidth
                  className={busy || !livekitConfigured ? "" : FILLED}
                  disabled={!livekitConfigured}
                  loading={busy}
                  icon={<Icon name="play" size={20} />}
                  onClick={handleGoLive}
                >
                  {t("liveStream.goLive")}
                </Button>
              )}

              {isLive && (canHost || isAdmin) && (
                <>
                  <Button
                    fullWidth
                    className={busy || !livekitConfigured ? "" : FILLED}
                    disabled={!livekitConfigured}
                    loading={busy}
                    icon={<Icon name="video" size={20} />}
                    onClick={handleRejoinAsHost}
                  >
                    {t("liveStream.rejoinHost")}
                  </Button>
                  <Button fullWidth variant="gray" disabled={busy} onClick={handleEndLive}>
                    {t("liveStream.endLive")}
                  </Button>
                </>
              )}

              {isLive && (
                <Button
                  fullWidth
                  variant={canHost || isAdmin ? "gray" : "filled"}
                  disabled={!livekitConfigured}
                  loading={busy}
                  onClick={handleJoinAsViewer}
                >
                  {t("liveStream.watchLive")}
                </Button>
              )}

              {!isLive && !(canHost || isAdmin) && (
                <p className="text-center text-subheadline text-label-secondary">{t("liveStream.waitingForHost")}</p>
              )}
            </div>
          </section>
        </div>
      )}
      <LiveStreamDeviceModal
        isOpen={!!devicePrompt}
        variant={devicePrompt?.variant === "stage" ? "stage" : "host"}
        isDark={isDark}
        onClose={() => {
          setDevicePrompt(null);
          stagePromptOpening.current = false;
        }}
        onConfirm={confirmDevicePrompt}
      />
    </MintSpaceAppWindow>
  );
}
